import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigation, useOutlet, UNSAFE_LocationContext } from 'react-router-dom';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SERVICES } from '../constants';

/*
 * Page transition: a brand-blue sheet and a dark panel sweep up over the page,
 * the panel shows where you are going ("Our clients"), the new page is swapped
 * in underneath, and the panels carry on up and away.
 *
 * The old page is kept on screen (with its own location frozen) until the
 * panels cover it, so nothing jumps. Lazy pages load while the panels are up.
 * Hash links on the same page (e.g. /#work) scroll without a transition.
 */
const COVER_MS = 760;
const REVEAL_MS = 650;

function labelFor(path) {
  if (path === '/' || path === '') return 'Home';
  if (path.startsWith('/services/')) return SERVICES.find((s) => s.link === path)?.title || 'Services';
  if (path.startsWith('/blog/')) return 'Blog';
  return (
    {
      '/services': 'Services',
      '/projects': 'Our clients',
      '/tech-stack': 'Tech stack',
      '/testimonials': 'Reviews',
      '/blog': 'Blog',
      '/contact': 'Contact',
      '/invoice': 'Invoice',
    }[path] || 'Farwings'
  );
}

export default function TransitionOutlet() {
  const location = useLocation();
  const navigation = useNavigation();
  const outlet = useOutlet();

  const [shownPath, setShownPath] = useState(location.pathname);
  const [phase, setPhase] = useState('idle'); // idle | cover | reveal
  const [label, setLabel] = useState('');
  const frozen = useRef({ outlet, location });
  const coverAt = useRef(0);
  const timers = useRef([]);
  const reduce = useRef(false);

  useEffect(() => {
    reduce.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return () => timers.current.forEach(clearTimeout);
  }, []);

  const changing = location.pathname !== shownPath;
  // keep a copy of the current page while nothing is changing
  if (!changing && navigation.state !== 'loading') frozen.current = { outlet, location };

  const startCover = (path) => {
    if (reduce.current) return;
    if (phase === 'cover') return;
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setLabel(labelFor(path));
    coverAt.current = performance.now();
    setPhase('cover');
  };

  // cover as soon as a navigation starts (lazy pages load under the panels)
  useEffect(() => {
    const to = navigation.location?.pathname;
    if (navigation.state === 'loading' && to && to !== shownPath) startCover(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation.state, navigation.location?.pathname]);

  // the new page is ready: wait until it is covered, swap, then reveal
  useEffect(() => {
    if (!changing) return;
    if (reduce.current) {
      setShownPath(location.pathname);
      if (!location.hash) window.scrollTo(0, 0);
      return;
    }
    startCover(location.pathname);
    const wait = Math.max(0, COVER_MS - (performance.now() - (coverAt.current || performance.now())));
    const t1 = setTimeout(() => {
      setShownPath(location.pathname);
      if (!location.hash) {
        if (window.lenis) window.lenis.scrollTo(0, { immediate: true, force: true });
        else window.scrollTo(0, 0);
      }
      requestAnimationFrame(() => {
        ScrollTrigger.refresh();
        setPhase('reveal');
      });
      const t2 = setTimeout(() => setPhase('idle'), REVEAL_MS + 60);
      timers.current.push(t2);
    }, wait || COVER_MS);
    timers.current.push(t1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const showOld = changing || (navigation.state === 'loading' && phase === 'cover');
  const page = showOld ? (
    <UNSAFE_LocationContext.Provider value={{ location: frozen.current.location, navigationType: 'POP' }}>
      {frozen.current.outlet}
    </UNSAFE_LocationContext.Provider>
  ) : (
    outlet
  );

  return (
    <>
      {page}
      <div aria-hidden="true" className={`pt-wrap pt-${phase}`}>
        <div className="pt-sheet" />
        <div className="pt-panel">
          <div className="pt-label">
            <img src="/farwings-mark.png" alt="" width="44" height="44" className="pt-mark" />
            <span className="display">{label}</span>
          </div>
        </div>
      </div>
    </>
  );
}
